import { useState } from "react";
import { toast } from "sonner";
import { X, LogOut, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { OrderTypeFields, validateOrder } from "@/components/TradeTerminal";

const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 }));

/** Broker-style square-off ticket for a Theta leg: qty, MARKET / LIMIT / SL / SL-M, cancels pending SL/target first. */
export default function ThetaCloseModal({ leg, pos, limitOnly, onClose, onDone }) {
  const [qty, setQty] = useState(pos.net_qty);
  const [type, setType] = useState(limitOnly ? "LIMIT" : "MARKET");
  const [price, setPrice] = useState(pos.ltp || "");
  const [trigger, setTrigger] = useState("");
  const [cancelPending, setCancelPending] = useState(true);
  const [busy, setBusy] = useState(false);
  const pending = [pos.sl, pos.tp].filter(Boolean);
  const lots = Math.round(Number(qty) / (leg.lot_size || 1) * 100) / 100;
  const pnl = pos.ltp != null && pos.buy_avg != null ? (pos.ltp - pos.buy_avg) * pos.net_qty : null;

  const submit = async () => {
    const err = validateOrder(type, price, trigger);
    if (err) { toast.error(err); return; }
    if (!(Number(qty) > 0) || Number(qty) > pos.net_qty) { toast.error("Quantity must be between 1 and your position size"); return; }
    setBusy(true);
    try {
      if (cancelPending) {
        for (const o of pending) {
          const { data } = await api.post(`/dhan/orders/${o.order_id}/cancel`);
          if (!data.ok) toast.warning(`Could not cancel ${o.order_type} order ${o.order_id}: ${data.detail || ""}`);
        }
      }
      const { data } = await api.post("/dhan/positions/exit", {
        security_id: pos.security_id, exchange_segment: pos.exchange_segment, net_qty: pos.net_qty, qty_units: Number(qty),
        order_type: type, price: Number(price) || 0, trigger_price: Number(trigger) || 0, confirm: true });
      if (data.ok) toast.success(`Exit order placed · ${data.order_status || data.order_id}`);
      else toast.error(data.detail || "Exit rejected");
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Exit failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[85] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="theta-close-modal">
      <div className="bg-[#0a0a0a] border border-zinc-800 max-w-md w-full">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#EF4444] font-mono">Square off · DHAN</div>
            <h2 className="font-heading text-lg font-bold uppercase tracking-tight">Close NIFTY {leg.strike} {leg.type}</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="theta-close-dismiss"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-4 text-sm">
          <div className="grid grid-cols-3 gap-2 text-xs font-mono">
            {[["Position", `${pos.net_qty} qty`], ["Avg buy", `₹${fmt(pos.buy_avg)}`], ["LTP", `₹${fmt(pos.ltp)}`]].map(([k, v]) => (
              <div key={k} className="border border-zinc-800 bg-[#121212] p-2"><div className="text-[9px] uppercase tracking-widest text-zinc-500">{k}</div><div className="font-bold">{v}</div></div>))}
          </div>
          {pnl != null && <div className="text-xs font-mono" data-testid="theta-close-pnl">Open P&L <span className={`font-bold ${pnl >= 0 ? "text-[#22C55E]" : "text-[#EF4444]"}`}>{pnl >= 0 ? "+" : ""}₹{fmt(pnl)}</span></div>}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Quantity (units) · {lots} lot{lots !== 1 ? "s" : ""}</label>
            <div className="mt-1 flex items-center gap-2">
              <input type="number" min="1" max={pos.net_qty} step={leg.lot_size || 1} value={qty} onChange={(e) => setQty(e.target.value)}
                className="w-28 bg-transparent border border-zinc-700 px-2 py-1.5 font-mono text-sm outline-none focus:border-[#007AFF]" data-testid="theta-close-qty" />
              <button onClick={() => setQty(pos.net_qty)} className="text-[10px] font-mono uppercase tracking-widest border border-zinc-700 px-2 py-1.5 hover:border-zinc-500" data-testid="theta-close-qty-all">All</button>
              <button onClick={() => setQty(Math.max(leg.lot_size || 1, Math.floor(pos.net_qty / 2 / (leg.lot_size || 1)) * (leg.lot_size || 1)))} className="text-[10px] font-mono uppercase tracking-widest border border-zinc-700 px-2 py-1.5 hover:border-zinc-500" data-testid="theta-close-qty-half">Half</button>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Order type{limitOnly ? " · after 3:15 PM only LIMIT / SL (SEBI CAS)" : ""}</label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <OrderTypeFields type={type} setType={setType} price={price} setPrice={setPrice} trigger={trigger} setTrigger={setTrigger} idPrefix="theta-close" limitOnly={limitOnly} />
              {pos.ltp != null && <button onClick={() => { setPrice(pos.ltp); setTrigger(pos.ltp); }} className="text-[10px] font-mono border border-zinc-700 px-2 py-0.5 hover:border-[#007AFF]" data-testid="theta-close-use-ltp">use LTP</button>}
            </div>
          </div>
          {pending.length > 0 && (
            <label className="flex items-start gap-2 border border-zinc-800 bg-[#121212] px-3 py-2 cursor-pointer" data-testid="theta-close-cancel-pending">
              <input type="checkbox" checked={cancelPending} onChange={(e) => setCancelPending(e.target.checked)} className="mt-0.5 accent-[#EF4444]" data-testid="theta-close-cancel-checkbox" />
              <span className="text-[11px] font-mono text-zinc-300">Cancel pending {pending.map((o) => `${o.order_type === "LIMIT" ? "TARGET" : "SL"} @ ₹${fmt(o.price)}`).join(" and ")} first
                <span className="block text-[9px] text-zinc-500 uppercase tracking-widest">prevents a double sell after you square off</span></span>
            </label>
          )}
          <button onClick={submit} disabled={busy} className="w-full text-xs uppercase tracking-widest bg-[#EF4444] hover:bg-[#F87171] disabled:opacity-50 text-black py-3 font-mono font-bold flex items-center justify-center gap-2" data-testid="theta-close-confirm">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
            {busy ? "Placing…" : `Sell ${qty} ${type === "MARKET" ? "@ market" : type === "LIMIT" ? `@ ₹${price || "—"}` : `${type === "STOP_LOSS" ? "SL" : "SL-M"} trig ₹${trigger || "—"}`}`}
          </button>
        </div>
      </div>
    </div>
  );
}
